import { notification } from "antd";
import { get, isEmpty, merge } from "lodash";
import omitDeep from "omit-deep-lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Loading from "shared/components/Spin";
import { useLazyQuery, useMutation } from "shared/hooks/useApi";
import {
  isSignedSubscription,
  persistProcessRuntime,
  resolveSignedResumeStatus,
} from "screens/Private/Broker/Projects/Detail/resolveProcessRuntime";
import { DOMAIN_SIGN_IN } from "utils/api/graphql/mutations/users";
import { LINK } from "utils/api/graphql/queries/link";
import { PROJECT } from "utils/api/graphql/queries/projects";
import { SOURCE_AVAILABILITY } from "utils/api/graphql/queries/sources";
import { TRACER } from "utils/api/graphql/queries/tracers";
import { KEYS, PROCESS_URL_PARAMS, SCREENS } from "utils/constants";
import { isUnavailable } from "../Unavailable/sourceAvailability";
import { buildDomainSignInData } from "./domainSignInUser";
import {
  buildProcessDomainName,
  readProcessInitParams,
  toQueryString,
} from "./processInitParams";
import {
  buildProjectInitialValues,
  persistGuestUserId,
  persistProcessParams,
  readProcessParams,
  resolveGuestUserId,
  resolveLinkId,
} from "./processParams";

const isValidParam = (value) => value && value !== "undefined";

const Init = () => {
  const navigate = useNavigate();
  const [signIn] = useMutation(DOMAIN_SIGN_IN, { fetchPolicy: "no-cache" });
  const { src } = useParams();
  const [host, setHost] = useState(null);
  const [getTracer] = useLazyQuery(TRACER);
  const [getProject] = useLazyQuery(PROJECT);
  const [getLink] = useLazyQuery(LINK);
  const [getSourceAvailability] = useLazyQuery(SOURCE_AVAILABILITY);

  const { queryParams, processParams, queryString, url } = useMemo(() => {
    const url = window.location.href;
    const queryParams = readProcessInitParams(url);
    return {
      queryParams,
      processParams: readProcessParams(queryParams),
      queryString: toQueryString(queryParams),
      url,
    };
  }, []);
  const [initialValues, setInitialValues] = useState(() =>
    buildProjectInitialValues(processParams),
  );

  const navigateToProject = useCallback(
    (tracer, projectId, subscriptionId, statusId, search = queryString) => {
      const cleanTracer = omitDeep(tracer, "__typename");
      const process = get(cleanTracer, "flow.process");
      persistProcessRuntime(projectId, process, cleanTracer);
      navigate(
        `${SCREENS.PRIVATE.GUEST.PROJECTS.DETAIL.path}/${projectId}${search}`,
        {
          state: {
            process,
            tracer: cleanTracer,
            subscriptionId: isValidParam(subscriptionId)
              ? subscriptionId
              : undefined,
            status: get(process, "processStatuses", []).find(
              ({ id }) => id === statusId,
            ),
          },
        },
      );
    },
    [navigate, queryString],
  );

  const navigateToCreate = useCallback(
    (user) => {
      const openCreate = () => {
        localStorage.setItem(KEYS.TEMPLATE_HISTORY, "{}");
        navigate(
          `${SCREENS.PUBLIC.PROCESSES.CREATE.path}/${src}${queryString}`,
          { state: { user, initialValues } },
        );
      };
      getSourceAvailability({
        fetchPolicy: "no-cache",
        variables: { where: { id: src } },
        onCompleted: ({ sourceAvailability }) => {
          const status = get(sourceAvailability, "status");
          if (!isUnavailable(status)) return openCreate();
          navigate(`${SCREENS.PUBLIC.PROCESSES.UNAVAILABLE.path}/${status}`, {
            replace: true,
          });
        },
        onError: openCreate,
      });
    },
    [navigate, src, queryString, initialValues, getSourceAvailability],
  );

  const openDuplicatedProject = useCallback(
    (project, user) => {
      const duplicatedUid = get(project, "uid");
      localStorage.setItem(KEYS.PROJECT_ID, project.id);
      localStorage.removeItem(KEYS.SUBSCRIPTION_ID);
      localStorage.removeItem(KEYS.STATUS_ID);
      localStorage.removeItem(KEYS.LINK_ID);

      const duplicatedParams = new URLSearchParams(queryParams);
      duplicatedParams.delete("l");
      if (duplicatedUid) duplicatedParams.set("uid", duplicatedUid);
      const duplicatedQueryString = toQueryString(duplicatedParams);

      const goToDuplicate = () =>
        getTracer({
          variables: { where: { source: { id: src } } },
          onCompleted: ({ tracer }) =>
            navigateToProject(
              tracer,
              project.id,
              undefined,
              undefined,
              duplicatedQueryString,
            ),
          onError: () => navigateToCreate(user),
        });

      if (!duplicatedUid) return goToDuplicate();

      signIn({
        variables: { data: buildDomainSignInData(host, duplicatedUid) },
        onCompleted: ({ domainSignIn }) => {
          localStorage.setItem(KEYS.ACCESS_TOKEN, domainSignIn?.accessToken);
          persistGuestUserId(duplicatedUid);
          goToDuplicate();
        },
        onError: () => navigateToCreate(user),
      });
    },
    [
      host,
      src,
      signIn,
      getTracer,
      queryParams,
      navigateToProject,
      navigateToCreate,
    ],
  );

  const handleProjectNavigation = useCallback(
    (user) => {
      const projectId = localStorage.getItem(KEYS.PROJECT_ID);
      const subscriptionId = localStorage.getItem(KEYS.SUBSCRIPTION_ID);
      const statusId = localStorage.getItem(KEYS.STATUS_ID);

      localStorage.removeItem(KEYS.USER);

      if (isValidParam(projectId)) {
        getProject({
          fetchPolicy: "no-cache",
          variables: { where: { id: projectId } },
          onCompleted: ({ project }) => {
            if (isEmpty(project)) {
              return navigateToCreate(user);
            }
            if (project.id !== projectId) {
              return openDuplicatedProject(project, user);
            }
            getTracer({
              variables: { where: { source: { id: src } } },
              onCompleted: ({ tracer }) => {
                const process = get(tracer, "flow.process");
                const subscription = (get(project, "subscriptions") || []).find(
                  (item) => get(item, "id") === subscriptionId,
                );
                const doneStatus = isSignedSubscription(subscription)
                  ? resolveSignedResumeStatus(process)
                  : null;
                const nextStatusId = get(doneStatus, "id") || statusId;
                if (get(doneStatus, "id")) {
                  localStorage.setItem(KEYS.STATUS_ID, doneStatus.id);
                }
                navigateToProject(
                  tracer,
                  projectId,
                  subscriptionId,
                  nextStatusId,
                );
              },
              onError: () => navigateToCreate(user),
            });
          },
          onError: () => navigateToCreate(user),
        });
      } else {
        navigateToCreate(user);
      }
    },
    [
      src,
      getProject,
      getTracer,
      navigateToProject,
      navigateToCreate,
      openDuplicatedProject,
    ],
  );

  useEffect(() => {
    const currentSource = localStorage.getItem(KEYS.SOURCE);
    if (currentSource && currentSource !== src) {
      localStorage.clear();
    }
    localStorage.setItem(KEYS.SOURCE, src);

    if (window.self === window.top) {
      persistProcessParams(processParams);
      const cuidParams = queryParams.get("cuid");

      setHost(buildProcessDomainName(url, cuidParams));
      return;
    }

    const handleMessage = (event) => {
      if (
        !process.env.REACT_APP_PROCESS_FLOWS.split(",").includes(event.origin)
      ) {
        return;
      }

      const { url: parentUrl } = event.data;
      const parsedUrl = new URL(parentUrl);
      const params = new URLSearchParams(parsedUrl.search);

      const subscriptionId = params.get("s");
      if (isValidParam(subscriptionId)) {
        localStorage.setItem(KEYS.SUBSCRIPTION_ID, subscriptionId);
      }

      const parentParams = readProcessParams(params);
      persistProcessParams(parentParams);
      setInitialValues((current) =>
        merge({}, current, buildProjectInitialValues(parentParams)),
      );
      setHost(parsedUrl.host);
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [src, url, queryParams, processParams]);

  useEffect(() => {
    if (!host) return;
    const userId = resolveGuestUserId(processParams);
    signIn({
      variables: { data: buildDomainSignInData(host, userId) },
      onCompleted: ({ domainSignIn }) => {
        localStorage.setItem(KEYS.ACCESS_TOKEN, domainSignIn?.accessToken);
        persistGuestUserId(userId);

        const link = resolveLinkId(processParams);

        const signedInUser = get(domainSignIn, "user");
        if (isValidParam(link)) {
          persistProcessParams({ [PROCESS_URL_PARAMS.LINK_ID]: link });
          getLink({
            fetchPolicy: "no-cache",
            variables: { where: { id: link } },
            onCompleted: ({ link }) => {
              const projectId = get(link, "project.id");
              const subscriptionId = get(link, "subscription.id");
              const statusId = get(link, "fields.status.id");

              if (projectId) localStorage.setItem(KEYS.PROJECT_ID, projectId);
              if (subscriptionId)
                localStorage.setItem(KEYS.SUBSCRIPTION_ID, subscriptionId);
              if (statusId) localStorage.setItem(KEYS.STATUS_ID, statusId);

              handleProjectNavigation(signedInUser);
            },
            onError: () => {
              handleProjectNavigation(signedInUser);
            },
          });
          return;
        }

        handleProjectNavigation(signedInUser);
      },
      onError: (error) => {
        notification.error({
          message: "Error signing in",
          description: error.message,
        });
      },
    });
  }, [host, signIn, processParams, getLink, handleProjectNavigation]);

  return <Loading />;
};

export default Init;
